import { useEffect, useState, useCallback } from "react";
import { Loader2, BadgeCheck, Check, X, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { StatusBadge, OVERLINE, H1, money, useAdmin } from "@/components/admin/ui";
import { BusinessDrawer } from "@/pages/admin/AdminBusinesses";

const TABS = [["pending", "Pending"], ["needs_info", "Needs Info"], ["approved", "Approved"], ["rejected", "Rejected"]];

export default function AdminApprovals() {
  const { admin } = useAdmin();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("pending");
  const [openId, setOpenId] = useState(null);
  const load = useCallback(() => { api.adminApprovals().then(setData).catch(() => setData({ counts: {}, all: [] })); }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (id, action, e) => {
    e.stopPropagation();
    try { await api.adminBusinessAction(id, { action }); toast.success("Updated"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const rows = (data?.all || []).filter((b) => b.approval_status === tab);

  return (
    <div className="p-6 md:p-8" data-testid="admin-approvals">
      <span className={OVERLINE}>Network</span>
      <h1 className={`${H1} mt-1`}>Approval Queue</h1>
      <p className="text-sm text-[#5C6B61] mt-2">Review businesses before they receive full marketplace privileges.</p>

      <div className="flex flex-wrap gap-1 mt-6 border border-[#E5E2DC] bg-white p-1 w-fit" data-testid="approval-tabs">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-3 py-1.5 text-xs font-semibold ${tab === k ? "bg-[#1F2A22] text-[#F9F8F6]" : "text-[#5C6B61] hover:bg-[#F2F0EB]"}`} data-testid={`approval-tab-${k}`}>
            {l} ({data?.counts?.[k] || 0})
          </button>
        ))}
      </div>

      {!data ? <div className="py-20 grid place-items-center"><Loader2 className="animate-spin text-[#1F2A22]" /></div>
        : rows.length === 0 ? <div className="mt-6 border border-dashed border-[#E5E2DC] bg-white p-16 text-center text-[#5C6B61]"><BadgeCheck size={28} className="mx-auto mb-2 text-[#E5E2DC]" />Nothing in “{TABS.find((t) => t[0] === tab)?.[1]}”.</div>
        : (
        <div className="mt-4 space-y-2" data-testid="approval-list">
          {rows.map((b) => (
            <div key={b.id} onClick={() => setOpenId(b.id)} className="bg-white border border-[#E5E2DC] px-4 py-3 flex items-center gap-3 flex-wrap cursor-pointer hover:bg-[#F9F8F6]" data-testid="approval-row">
              <div className="flex-1 min-w-0">
                <p className="font-medium text-[#1F2A22]">{b.name}</p>
                <p className="text-xs text-[#5C6B61]">{b.category} · {b.contact_email || "—"} · {money(b.stats?.gmv_cents)} GMV</p>
              </div>
              <StatusBadge status={b.approval_status} />
              {admin?.permissions?.includes("approvals") && (
                <div className="flex gap-1.5">
                  <button onClick={(e) => act(b.id, "approve", e)} className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 border border-[#1F2A22]/30 text-[#1F2A22] hover:bg-[#1F2A22] hover:text-white" data-testid="approval-approve"><Check size={12} /> Approve</button>
                  <button onClick={(e) => act(b.id, "needs_info", e)} className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 border border-[#FF6A00]/40 text-[#FF6A00] hover:bg-[#FF6A00] hover:text-white" data-testid="approval-needsinfo"><HelpCircle size={12} /> Needs Info</button>
                  <button onClick={(e) => act(b.id, "reject", e)} className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 border border-[#D9381E]/40 text-[#D9381E] hover:bg-[#D9381E] hover:text-white" data-testid="approval-reject"><X size={12} /> Reject</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {openId && <BusinessDrawer id={openId} onClose={() => setOpenId(null)} onChanged={load} />}
    </div>
  );
}
